/**
 * FillComponent is a part of the gameplay, responsible for managing
 * user interactions in a fill-in-the-blanks style game.
 * Users drag or select answers from a choice list and fill in missing
 * parts of the answer list.
 * It manages animations, answer verification, and gameplay state changes.
 */
import { Component, isDevMode, OnInit, OnDestroy } from '@angular/core'
import { NgClass, NgStyle, NgTemplateOutlet } from '@angular/common';

import {
  fadeInAnimation,
  fadeOutAnimation,
  fadeInOnEnterAnimation,
  fadeOutOnLeaveAnimation,
  headShakeAnimation,
  shakeAnimation,
} from 'angular-animations'

import { debounceTime, delay, from, map, merge, Subject, takeUntil } from 'rxjs'

import {
  SafePipe,
  PlayButtonComponent,
  SlidePanelComponent
} from '@nx-superprep/shared'

import { FrontendCard as Card, Choice, PartialChoice, Answer, ChoiceType, GameplayComposeFullAnswer } from '@nx-superprep/backend/firebase'

import { GameplayBaseComponent, gameplayBaseStyling, gameplayReplace, gameplaySpacing } from '../gameplay-base'
//import { synthesis } from 'simple-speech'
import { startsWith } from 'lodash-es';
import { isMandarin } from '@nx-superprep/utils';
import { MatCardModule } from '@angular/material/card'
import { MatIconModule } from '@angular/material/icon'
import { MatButtonModule } from '@angular/material/button'

class FillElement extends PartialChoice<AnswerPart> {}

@Component({
  standalone: true,
  selector: 'gameplay-fill',
  templateUrl: './fill.component.html',
  styleUrls: ['./fill.component.scss'],
  animations: [
    fadeInAnimation(),
    fadeOutAnimation(),
    fadeInOnEnterAnimation(),
    fadeOutOnLeaveAnimation(),
    headShakeAnimation({ duration: 900, delay: 0 }),
    shakeAnimation({ duration: 200, delay: 0, translate: '3px' }),
  ],
  imports: [
    NgClass, NgStyle, NgTemplateOutlet,
    SafePipe,
    MatCardModule,
    MatIconModule,
    MatButtonModule,
    SlidePanelComponent,
    PlayButtonComponent
  ]
})
export class FillComponent extends GameplayBaseComponent implements OnInit, OnDestroy {
  override title = 'fill'

  /**
   * Stores the list of selectable answer parts (choices) that the user can drag into the answer zone.
   * These are derived from the card's template choices and dynamically shuffled for each round.
   */
  private _choiceList: AnswerPart[] = []
  public get choiceList(): AnswerPart[] {
    return this._choiceList
  }
  public set choiceList(value: AnswerPart[]) {
    this._choiceList = value
  }
  /**
   * Holds the current set of answers filled in by the user.
   * Each AnswerPart represents a slot in the answer that can either be filled by the user or remain a placeholder.
   */
  private _answerList: AnswerPart[] = []
  public get answerList(): AnswerPart[] {
    return this._answerList
  }
  public set answerList(value: AnswerPart[]) {
    this._answerList = value
  }

  lists: { name:'answerList'|'choiceList', items: AnswerPart[]}[] = []

  private _isFullAnswer = true

  private readonly onEnter = new Subject<void>()


  /**
   * Stores the elements that have been selected by the user during the gameplay session.
   * This tracks which parts of the answer have been filled and whether they are correct.
   */
  private _selectedElements: FillElement[] = []
  private _animationCount = 0

  private _placeholder = gameplaySpacing.spacer

  isAnimatedList(list: { name: string }) {
    return  list.name === 'answerList' && this.isAllValuesFilled && !this.isAnswer
  }

  /**
   * Handles when an animation starts, setting animation counters based on the type of animation.
   * If it's a 'headShake' animation, we double the counter to prevent premature conclusion of the animation sequence.
   *
   * @param e - Animation event object, which contains the trigger name.
   */
  onAnimationStart(e: { triggerName: string }) {
    this._animationCount = e.triggerName === 'headShake' ? 2 : 1
  }

  /**
   * Determines whether the current step in the animation matches the provided value.
   * This is useful for controlling when to trigger certain UI changes based on the animation's progress.
   *
   * @param value - The step number to check against the current animation count.
   * @returns boolean - True if the current step matches the provided value.
   */
  isAnimationStep(value: number) {
    return this.isAllValuesFilled && !this.isAnswer && this.animationCount === value
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  override onAnimationDone(_e: unknown) {
    this._animationCount--
  }

  get isAnimationDone() {
    return this._animationCount <= 0
  }

  get animationCount() {
    return this._animationCount
  }

  get selectedValues() {
    return this._selectedElements.filter(e => e.value ).map(e => e.value)
  }

  get isAllValuesFilled() { return this.answerList.every(a => a.isVisible) }

  get isAnswer() {
    return this._selectedElements.every(e => e.isCorrect)
  }

  get answerValue() {
    return this.answer?.textUrl ?? ''
  }

  override get isAudioOnly() {
    return !!this.question
      && this.question.hasAudio
      && this.isDotted(this.question.textUrl)
      && this.question.bottomText
      .replace(gameplayReplace.ell, '')
      .replace(gameplayReplace.punctuation, '')
      .length === 0
  }

  get shouldShowQuestionText() {
    return (this.question?.hasImage || this.question?.hasText)
      && (!this.isAudioOnly || !this.isDotted(this.question.textUrl))
  }

  get shouldShowSubmitButton() {
    return false // this.isComplete && !this.didAnswer
  }

  isDotted(text: string) {
    return (text.match(gameplayReplace.ell)??[]).length > 0
  }

  choiceText() {
    return this.answerList.map(a => a.value).join(' ')
  }

  choiceName() {
    return this.answerList.filter(a => !a.disabled).map(a => a.value).join('|')
  }

  /**
   * Sets up listeners for user interactions using RxJS.
   * The debounceTime ensures that rapid selections do not trigger multiple events in quick succession.
   * The merge() method listens for both component destruction and cancellation events to properly clean up subscriptions.
   */
  listen() {
    this.onCancel.next()
    from(this.onEnter).pipe(
      takeUntil(merge(this.onDestroy, this.onCancel)),
      debounceTime(500),
      map(() => this.isAnswer ? this.answer : new Choice({type: ChoiceType.wrong, name: this.selectedValues.join('|')})),
      delay(500)
    ).subscribe((choice) => {
      this.onSelect(choice)
    })
  }

  /**
   * Cleans up resources when the component is destroyed.
   * Completes the onEnter subject and calls the parent ngOnDestroy method.
   */
  override ngOnDestroy() {
    // Complete the onEnter subject to free resources
    this.onEnter.complete();
    super.ngOnDestroy();
  }

 /**
  * Initializes the state of the component when a new card is loaded.
  * Configures the choice and answer lists, prepares animations, and starts listeners for user interactions.
  *
  * @param card - The current card object containing gameplay data.
  * @returns boolean - Indicates if the initialization was successful.
  */
  override initState(card: Card) {
    if (!super.initState(card)) { return false }
    this._isFullAnswer = GameplayComposeFullAnswer.includes(card.gameplay.name)
    this._animationCount = 0
    this.initTuple(card)
    this._selectedElements = (this.answer?.isMultiple
      ? (this.answer?.tuple ?? []) as string[]
      : [this.answer?.textUrl ?? '']).map((e, i) => new FillElement({choice: new AnswerPart({name:e, position:i}), answer: e, value: '', length: e.length}))
    this.lists = [{name:'answerList', items: this.answerList}, {name: 'choiceList', items: this.choiceList}]
    this.listen()
    return true
  }

  override resetState(card: Card | undefined) {
    super.resetState(card)
    this.initTuple(card)
    this._selectedElements.forEach(e => e.reset())
  }

  initTuple(card: Card | undefined) {
    if (!card || !card.answer) return;

    const answer = card.answer;
    const tuple = answer.tuple;
    const choices = card.template.choices;

    // Create choice list
    this.choiceList = choices
      .filter(choice => choice.isWrong || (choice instanceof Answer && !choice.isMultiple))
      .map((choice, position) => new AnswerPart({ name: choice.name, position, value: choice.name, connected: this.answerList }));

    const choiceNames = this.choiceList.map(choice => choice.name);

    // Create answer list
    const answerTemplate = this._isFullAnswer ? tuple.map(() => this._placeholder).join('') : card.question?.bottomText ?? '';
    const placeholders = answerTemplate.split(this._placeholder, (this._isFullAnswer ? 0 : 1) + tuple.length)
      .flatMap((word, index, array) => {
        const trimmedWord = word.trim();
        return trimmedWord.length > 0 ? (index + 1 < array.length ? [trimmedWord, this._placeholder] : [trimmedWord]) : [this._placeholder];
      });

    this.answerList = placeholders.map((value, position) => {
      const visible = value !== this._placeholder;
      const name = visible ? value : choiceNames.shift() ?? '';
      return new AnswerPart({ name, position, value, disable: true, visible, connected: this.choiceList });
    });

    this.choiceList.shuffle();
    this.choices = card.template.choices ?? [];
  }

  getCompound(text: string, elements: FillElement[], isCorrect: boolean) {
    const _l = elements.length
    const _style = isCorrect ? gameplayBaseStyling.answerRightStyle : gameplayBaseStyling.answerWrongStyle
    return _l > 0 && text.match(gameplayBaseStyling.ellipsis)
    ? text
      .split(gameplayBaseStyling.ellipsis)
      .map((s, i) => s + (i < _l ? isCorrect ? elements[i].displayAnswer : elements[i].displayValue : ''))
      .join('')
      .replace(gameplayReplace.answerStyle, _style)
    : text
  }

  override questionText() {
    return this._isFullAnswer ? (this.question?.textUrl.replace(gameplayReplace.tag, '').length !== 0 ? super.questionText() : '') :  ''
  }

  override answerText() {
    return this.getCompound(super.bottomText() as string, this._selectedElements, true)
  }

  override bottomText() {
    return this.getCompound(super.bottomText() as string, this._selectedElements, this.isAnswer)
  }

  selectedText() {
    return this._selectedElements.map((e) => e.value).join('')
  }

  isActiveItem(item: AnswerPart) {
    return this.answerList.find(a => !a.isSet)?.name === item.name
  }

  async clickDrop(item: AnswerPart, src: string) {
    await this.move(item,
      this.lists[src === this.lists[0].name ? 0 : 1].name,
      this.lists[src === this.lists[0].name ? 1 : 0].name
    )
  }

  move(item: AnswerPart, ...targets: string[]) {
    let result = Promise.resolve()
    if (this.didAnswer) { return result }
    const src = Object(this)[targets[0]] as AnswerPart[]
    const dst = Object(this)[targets[1]] as AnswerPart[]
    const srcItem = src.find(s => s.isVisible && s.value === item.value && s.position === item.position)
    if (!srcItem || srcItem.disabled) { return result }
    if (targets[0] === 'answerList') { // from answerZone
      srcItem.value = srcItem.placeholder
      srcItem.toggle('visible','disable')
      // const dstItem = dst.find(d => d.name === srcItem.name)
      // dstItem.toggle('disable'); // disable once selected
      // remove from selected
      const found = this._selectedElements.findIndex(e => e.answer === srcItem.name)
      if (found >= 0) { this._selectedElements[found].value = undefined }
    } else { // from choiceList
      const dstItem = dst.find(d => !d.isVisible)
      if (!dstItem) { return result }
      dstItem.value = srcItem.value
      dstItem.toggle('visible','disable')
      // srcItem.toggle('disable'); // disable once selected
      // insert in selected
      const found = this._selectedElements.findIndex(e => !e.isComplete)
      if (found >= 0) {
        this._selectedElements[found].value = srcItem.value
        result = this.speak(srcItem.value)
      }
    }
    return result.finally(() => { if (!this.shouldShowSubmitButton) { this.onSubmit()}} )
  }

  onSubmit() {
    if (this.isAllValuesFilled) { this.onEnter.next() }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  speak(value: string) {
    return isDevMode()
    ? Promise.resolve() //synthesis?.use({lang:this.lang(value)})?.speak(value).catch(() => Promise.resolve()) ?? Promise.resolve()
    : Promise.resolve()
  }

  lang(value: string) {
    // infer from value
    if (isMandarin(value)) { return 'zh-CN' }
    // default to english
    if (!this.card) { return 'en-US' }
    // try to infer from moduleId
    if (startsWith(this.card.template.moduleId, 'CHIN')) { return 'zh-CN' }
    if (startsWith(this.card.template.moduleId, 'SPAN')) { return 'es-ES' }
    if (startsWith(this.card.template.moduleId, 'SP')) { return 'es-ES' }
    if (startsWith(this.card.template.moduleId, 'FREN')) { return 'fr-FR' }
    if (startsWith(this.card.template.moduleId, 'FR')) { return 'fr-FR' }
    // fall back to language of card
    switch (this.card.template.language) {
      case 'FR': return 'fr-FR'
      case 'EN': return 'en-US'
      case 'CH': return 'zh-CN'
      default: return 'en-US'
    }
  }

  // lifecycle methods

  override onForceAnswer(isCorrect = Math.random() < 0.5) {
    this._selectedElements.forEach(e => e.forceAnswer(isCorrect))
    this.onEnter.next()
  }
}

class AnswerPart {
  name: string
  position: number
  connected: AnswerPart[]

  private _value: string
  private _visible = true
  private _disable = true
  private _placeholder: string

  constructor(options: { name: string; position: number; value?: string; connected?: AnswerPart[]; disable?: boolean; visible?: boolean; placeholder?: string }) {
    this.name = options.name
    this.position = options.position
    this._value = options.value ?? ''
    this._disable = options.disable ?? false
    this._visible = options.visible ?? true
    this.connected = options.connected ?? []
    this._placeholder = options.placeholder ?? gameplaySpacing.spacer
  }

  get disabled() { return this._disable }
  get value() { return (this._visible ? this._value : this._placeholder) }
  set value(value) { this._value = value }
  get placeholder() { return this._placeholder }

  get isCorrect() { return this._value === this.name }
  get isVisible() { return this._visible }
  set isVisible(value) { this._visible = value }
  get isSet() { return Boolean(this._value) && this._value !== this._placeholder }

  getValue(hide: boolean) {
    return this.isSet ? this.value : hide ? '...' : this.value
  }

  toggle(...args: string[]) {
    if (args.includes('visible')) { this._visible = !this._visible }
    if (args.includes('disable')) { this._disable = !this._disable }
  }
}
